/* ═══════════════════════════════════════════════════════════════
   Spec Masters Studio — the gutter grab-handles
   (Phase 1 item 4, docs/MASTERS_UNIFIED_PLAN.md).

   Point at a block and a grip appears in the sheet's left margin.
   Click it to select that block, Ctrl-click to toggle, Shift-click to
   take the range — the app-wide Explorer rules, running along DOCUMENT
   order (`lib/listSelection.ts` via `blockSelect.ts`). Drag a grip and
   the WHOLE selection travels: a line shows the boundary it will land
   on, and the drop is one ProseMirror transaction, so one Ctrl+Z puts
   the document back.

   Nothing here touches the caret. Typing, arrows, shift-arrow and
   double-click-a-word stay exactly what ProseMirror makes them; the
   block selection is a second, DISCRETE layer that only whole-block
   commands read (`designer/commands.ts → selectedBlocks`).

   DESIGN §4: portalled to <body>, position: fixed, anchored from
   getBoundingClientRect(), so a zoomed or scrolled sheet cannot clip
   the grips. Like the table grips, they are a SHORTCUT and never the
   only path — every operation they reach also lives in the right-click
   menu and on the keyboard (Alt+Shift+Arrow moves the same blocks).

   Being portalled cuts both ways: a MOUSE press on a grip never moves
   DOM focus there (`beginDrag` preventDefaults the mousedown), so if
   focus was outside the editor already — a toolbar, a panel, the
   outline search box — the keyboard chords that follow (Ctrl+G, Delete,
   the clipboard triad…) land nowhere, because every one of them is
   either the editor's own keymap or a native clipboard event on the
   editor's DOM. `focusSheet` below is the one repair: every mouse-driven
   selection puts focus back on the sheet, without moving the caret, the
   block selection or the scroll position.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { createPortal } from 'react-dom';
import type { Editor } from '@tiptap/react';
import { GripVertical } from 'lucide-react';
import { Icon } from '../components/ui/Icon';
import {
  blockIdsInTextSelection,
  blockSelection,
  clearBlockSelection,
  clickBlockHandle,
  moveBlocksTo,
  setBlockSelection,
} from './blockSelect';
import {
  GROUP_MODULE_REFUSAL,
  groupSelectedBlocks,
  moveSelectedBlocks,
  pasteFromSystemClipboard,
  runClipboardCommand,
  ungroupSelectedBlocks,
} from './commands';
import { deleteSelectedBlocks, expandToGroups } from '../designer/commands';
import {
  dropModuleBlocksOnPart,
  insertModuleArticle,
  isUnderArticleLine,
  moveModuleBlocksTo,
  NEW_PARAGRAPH_DRAG_MIME,
  partLandingIndex,
} from './moduleBuild';
import type { ModulePart } from '../../shared/spec-modules';
import { insertionPiece, topBlocks } from '../designer/moduleInsert';
import { snapBoundaryOutOfGroup } from '../../shared/block-groups';

interface Props {
  editor: Editor;
  canEdit: boolean;
  /** MODULE MODE (Cohesion R1 Phase 1). A drag that lands an article among a
   *  different part's articles sets `modulePart` in the same move — the
   *  margin part control this replaces used to be the only way to do that. */
  moduleMode?: boolean;
  /** Say something the user needs to hear — a grip holding keyboard focus
   *  routes Ctrl+G by hand (below) and needs the same "a group would cut a
   *  module insertion in half" toast the editor keymap raises. Absent ⇒ the
   *  refusal stays silent, same as the canvas's own `onNotice?`. */
  onNotice?: (msg: string) => void;
}

/** How far left of the text column the grip sits, and how wide the band is
 *  that still counts as "pointing at this block" — sized against the
 *  sheet's own LEFT margin (`--sps-sheet-margin-l`, Studio.css), which is
 *  kept wide enough on purpose to hold the grip's full hit box without
 *  ever overlapping the text. */
const GRIP_X = 26;
const GRIP_BAND = 48;
/** The group bracket sits between the grip and the text column. */
const BRACKET_X = 14;
/** A press has to travel this far before it is a drag and not a click. */
const DRAG_THRESHOLD = 4;

interface Spot {
  id: string;
  x: number;
  y: number;
  selected: boolean;
  /** How many blocks this grip speaks for: 1, or the whole group's size. */
  size: number;
}

/** The bracket drawn beside a group's blocks — the non-colour half of the
 *  "these travel together" signal (DESIGN §7: colour is never the only cue). */
interface Bracket {
  groupId: string;
  x: number;
  top: number;
  height: number;
}

interface DropLine {
  index: number;
  y: number;
  left: number;
  width: number;
}

interface DragState {
  ids: string[];
  startX: number;
  startY: number;
  moved: boolean;
  /** Block rects frozen at drag start — the document cannot change mid-drag,
   *  and re-measuring every block on every mousemove is what makes a long
   *  document's drag stutter. */
  rects: Array<{ id: string; top: number; bottom: number; group: string; level: number }>;
  column: { left: number; width: number };
}

/** Every top-level block element, in document order. Widget decorations (the
 *  stacked choice-point cards) are children too, and carry no block id. */
function blockEls(editor: Editor): HTMLElement[] {
  const dom = editor.view.dom as HTMLElement;
  return Array.from(dom.children).filter(
    (el): el is HTMLElement => el instanceof HTMLElement && el.hasAttribute('data-block-id')
  );
}

/** A block element's group, or '' — `data-group-id` is written by the node's
 *  own attribute (designer/nodeBase.ts), so the overlay reads the document's
 *  truth off the same element it is measuring. */
function groupOfEl(el: HTMLElement): string {
  return el.getAttribute('data-group-id') || '';
}

/**
 * A drop can never land INSIDE a group — the same rule `moveBlocksTo` applies
 * to the document, applied here to the LINE so the pointer is never promised a
 * landing the transaction will refuse (`shared/block-groups.ts`, one rule for
 * both). Blocks that are themselves travelling do not count: a group dropped
 * back into its own gap splits nothing.
 */
function snapLineIndex(
  rects: Array<{ id: string; group: string; level: number }>,
  index: number,
  moving: Set<string>,
  /** Module canvas, paragraph-led drag (TWEAKS item 10): the line directly
   *  under an article heading is always a target, group or not. */
  underArticleIsLegal = false
): number {
  const levels = underArticleIsLegal ? rects.map((r) => r.level) : null;
  return snapBoundaryOutOfGroup(rects, index, {
    moving,
    legal: levels ? (i) => isUnderArticleLine(levels, i) : undefined,
  });
}

/** The three PART header decorations (`moduleBuild.ts`'s `headerWidget`),
 *  read straight off the DOM — module mode only, and only ever three of
 *  them. Each one renders exactly at its group's start (or, for an empty
 *  part, at the spot that group's first article would land), so its own
 *  rect IS that zone's drop target (Module Zones Phase 2/3). */
function headerEls(editor: Editor): Array<{ part: ModulePart; el: HTMLElement }> {
  const dom = editor.view.dom as HTMLElement;
  return Array.from(dom.querySelectorAll<HTMLElement>('[data-sf-part-hdr]')).map((el) => ({
    part: Number(el.getAttribute('data-sf-part-hdr')) as ModulePart,
    el,
  }));
}

/**
 * A header's VISUAL bounds — the union of its title row and (empty part)
 * hint, NEVER the wrapper's rect.
 *
 * The wrapper carries `margin-top: var(--space-4)` plus `padding:
 * var(--space-3) 0 var(--space-2)` (Studio.css), so its rect is a ~60px
 * band that swallows the whole GAP between two part groups — precisely
 * where a person aims when they mean "the end of this part" or "the top of
 * that one". Hit-testing against that band captured almost every drop near
 * a boundary and silently redirected it to "end of the header's part",
 * which is why dragging an existing block "almost never worked". Only the
 * ink a person can actually see counts as pointing AT the header; the
 * padding/margin around it belongs to the precise line drop.
 */
function headerHitRect(el: HTMLElement): { top: number; bottom: number } {
  const rows = el.querySelectorAll<HTMLElement>('.sps-mod-part-hdr-text, .sps-mod-part-hdr-hint');
  if (!rows.length) {
    const r = el.getBoundingClientRect();
    return { top: r.top, bottom: r.bottom };
  }
  let top = Infinity;
  let bottom = -Infinity;
  rows.forEach((row) => {
    const r = row.getBoundingClientRect();
    top = Math.min(top, r.top);
    bottom = Math.max(bottom, r.bottom);
  });
  return { top, bottom };
}

/** How much of a header's box, at its top and bottom edge, still belongs
 *  to the precise line drop around it (the v2.19.18 rule: a line target
 *  wins where both could match). The rest of the box — the whole visible
 *  header row, not just its ink — is the header target (TWEAKS item 10). */
const HEADER_EDGE = 6;

/** Which header (if any) a screen Y coordinate lands on — the visible row
 *  (its ink, widened to the box minus a thin edge band), so a precise line
 *  drop in the gap around a header always wins. */
function headerAt(
  clientY: number,
  headers: Array<{ part: ModulePart; el: HTMLElement }>
): { part: ModulePart; el: HTMLElement } | null {
  for (const h of headers) {
    const ink = headerHitRect(h.el);
    const box = h.el.getBoundingClientRect();
    const top = Math.min(ink.top, box.top + HEADER_EDGE);
    const bottom = Math.max(ink.bottom, box.bottom - HEADER_EDGE);
    if (clientY >= top && clientY <= bottom) return h;
  }
  return null;
}

/** The block-line index a screen Y coordinate is nearest — live DOM query,
 *  for the drags that have no "frozen at drag start" rect list of their own
 *  (the toolbox's native "New paragraph" drag). */
function liveLineIndex(editor: Editor, clientY: number): number {
  const els = blockEls(editor);
  for (let i = 0; i < els.length; i++) {
    const r = els[i].getBoundingClientRect();
    if (clientY < r.top + r.height / 2) return i;
  }
  return els.length;
}

/**
 * Put DOM focus back on the sheet after a MOUSE-driven grip press, without
 * moving the caret, the block selection or the scroll position.
 *
 * `view.focus()` (not `editor.commands.focus()`, which defers to a React
 * frame) calls ProseMirror's own `focusPreventScroll` and re-syncs the DOM
 * selection to the existing PM selection — it never dispatches a
 * transaction, so the block-selection plugin's state (a separate layer, see
 * the file header) is untouched. This is the one repair every
 * focus-dependent chord needed: Ctrl+G, Ctrl+Shift+G and Alt+Shift+Arrow
 * live in the editor's own keymap, and Delete/Backspace/Ctrl+C/X/V are the
 * `BlockClipboard` extension's keymap + native clipboard-event handlers
 * (`commands.ts`) — every one of them only ever sees a keystroke when the
 * EDITOR, not the toolbar/panel/menu the mouse came from, holds focus.
 */
function focusSheet(editor: Editor) {
  editor.view.focus();
}

export function BlockGutter({ editor, canEdit, moduleMode, onNotice }: Props) {
  // Every transaction can move a block, change the selection or re-render the
  // sheet, so the overlay re-derives from the editor rather than keeping a
  // second copy of the truth.
  const [tick, bump] = React.useReducer((n: number) => n + 1, 0);
  React.useEffect(() => {
    editor.on('transaction', bump);
    return () => {
      editor.off('transaction', bump);
    };
  }, [editor]);

  const [hot, setHot] = React.useState<string | null>(null);
  const [spots, setSpots] = React.useState<Spot[]>([]);
  const [brackets, setBrackets] = React.useState<Bracket[]>([]);
  const [drop, setDrop] = React.useState<DropLine | null>(null);
  const dragRef = React.useRef<DragState | null>(null);

  const sel = blockSelection(editor.state);
  const selKey = sel.ids.join('|');
  const dispatch = React.useMemo(() => editor.view.dispatch.bind(editor.view), [editor]);

  /* ─── Which block is under the pointer ─── */
  React.useEffect(() => {
    if (!canEdit) {
      setHot(null);
      return;
    }
    const onMove = (e: MouseEvent) => {
      if (dragRef.current) return;
      const target = e.target as HTMLElement | null;
      if (!target || typeof target.closest !== 'function') return;
      // Over one of our own grips — keep showing exactly what is showing.
      if (target.closest('.sps-blk-gutter')) return;
      const dom = editor.view.dom as HTMLElement;
      let el = target.closest<HTMLElement>('[data-block-id]');
      if (el && !dom.contains(el)) el = null;
      if (!el) {
        // In the margin band to the left of the text: probe straight across,
        // so travelling out to the grip never makes it vanish first.
        const r = dom.getBoundingClientRect();
        const inBand =
          e.clientX >= r.left - GRIP_BAND &&
          e.clientX < r.left &&
          e.clientY >= r.top &&
          e.clientY <= r.bottom;
        if (inBand) {
          const probe = document.elementFromPoint(r.left + 4, e.clientY) as HTMLElement | null;
          const hit = probe?.closest?.<HTMLElement>('[data-block-id]') || null;
          if (hit && dom.contains(hit)) el = hit;
        }
      }
      const id = el?.getAttribute('data-block-id') || null;
      setHot((cur) => (cur === id ? cur : id));
    };
    document.addEventListener('mousemove', onMove, true);
    return () => document.removeEventListener('mousemove', onMove, true);
  }, [editor, canEdit]);

  /* ─── Where the grips sit ─── */
  const placeRef = React.useRef<() => void>(() => {});
  React.useLayoutEffect(() => {
    if (!canEdit) {
      placeRef.current = () => {};
      setSpots([]);
      setBrackets([]);
      return;
    }
    const place = () => {
      const dom = editor.view.dom as HTMLElement;
      const column = dom.getBoundingClientRect();
      const els = blockEls(editor);
      const wanted = new Set<string>(sel.ids);
      if (hot) wanted.add(hot);
      // A GROUP answers as one: pointing at any block of it lights the whole
      // run, and the run gets ONE grip — at its first block — plus a bracket
      // spanning it. Per-block grips inside a group would promise an
      // independence the blocks do not have.
      const wantedGroups = new Set<string>();
      for (const el of els) {
        const id = el.getAttribute('data-block-id') || '';
        const gid = groupOfEl(el);
        if (gid && id && wanted.has(id)) wantedGroups.add(gid);
      }
      const groupSize = new Map<string, number>();
      for (const el of els) {
        const gid = groupOfEl(el);
        if (gid) groupSize.set(gid, (groupSize.get(gid) || 0) + 1);
      }

      const next: Spot[] = [];
      const bars: Bracket[] = [];
      let seenGroup = '';
      for (const el of els) {
        const id = el.getAttribute('data-block-id') || '';
        const gid = groupOfEl(el);
        const firstOfGroup = !!gid && gid !== seenGroup;
        seenGroup = gid;
        const shown = !!id && (wanted.has(id) || (!!gid && wantedGroups.has(gid)));
        if (!shown) continue;
        const r = el.getBoundingClientRect();
        if (gid) {
          const bar = bars.find((b) => b.groupId === gid);
          if (bar) bar.height = Math.max(bar.height, r.bottom - bar.top);
          else
            bars.push({ groupId: gid, x: column.left - BRACKET_X, top: r.top, height: r.height });
        }
        if (gid && !firstOfGroup) continue;
        next.push({
          id,
          x: column.left - GRIP_X,
          // On the block's FIRST line, not its middle: a nine-line paragraph
          // whose grip floats halfway down reads as belonging to nothing.
          y: Math.min(r.top + r.height / 2, r.top + 14),
          selected: sel.ids.includes(id),
          size: gid ? groupSize.get(gid) || 1 : 1,
        });
      }
      setSpots((cur) =>
        cur.length === next.length &&
        cur.every(
          (s, i) =>
            s.id === next[i].id &&
            s.x === next[i].x &&
            s.y === next[i].y &&
            s.selected === next[i].selected &&
            s.size === next[i].size
        )
          ? cur
          : next
      );
      setBrackets((cur) =>
        cur.length === bars.length &&
        cur.every(
          (b, i) =>
            b.groupId === bars[i].groupId &&
            b.x === bars[i].x &&
            b.top === bars[i].top &&
            b.height === bars[i].height
        )
          ? cur
          : bars
      );
    };
    // Re-measured whenever the picked set, the hovered block or the DOCUMENT
    // moves (`tick` counts transactions), and followed through scroll/resize
    // by the listener below — the grips are fixed-positioned, so their anchor
    // has to be re-read, never remembered.
    placeRef.current = place;
    place();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit, editor, selKey, hot, tick]);

  React.useEffect(() => {
    const follow = () => placeRef.current();
    window.addEventListener('resize', follow);
    window.addEventListener('scroll', follow, true);
    return () => {
      window.removeEventListener('resize', follow);
      window.removeEventListener('scroll', follow, true);
    };
  }, []);

  /* ─── Escape clears the block selection ─── */
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (!blockSelection(editor.state).ids.length) return;
      // Only when there IS a block selection: otherwise Escape stays what the
      // spec keymap makes it — the keyboard user's way out of the document.
      e.preventDefault();
      e.stopPropagation();
      clearBlockSelection(editor.state, dispatch);
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [editor, dispatch]);

  /* ─── A plain click on the sheet clears it ─── */
  React.useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      const target = e.target as HTMLElement | null;
      if (!target || typeof target.closest !== 'function') return;
      if (target.closest('.sps-blk-gutter')) return;
      // Fenced to the canvas: pressing a toolbar button or a menu item must
      // still act on the blocks the user picked, so those never clear it.
      if (!target.closest('.sps-canvas')) return;
      clearBlockSelection(editor.state, dispatch);
    };
    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [editor, dispatch]);

  /* ─── Drag a grip ───
     `piece` (modules-polish item 3): a module piece's NAME TAG is a handle
     too. It drags exactly the run the tag names, through this same drag —
     one line, one `moveBlocksTo`, one Ctrl+Z — held inside the piece's own
     Part (`bounds`), and a press that never travels is left to the tag's own
     click (select the piece, show it in the panel). */
  const beginDrag = (
    e: React.MouseEvent | MouseEvent,
    id: string,
    piece?: { ids: string[]; bounds: [number, number] }
  ) => {
    if (e.button !== 0 || !canEdit) return;
    e.preventDefault();
    e.stopPropagation();

    const picked = blockSelection(editor.state).ids;
    const spanned = blockIdsInTextSelection(editor.state);
    // What travels: the block selection if this grip is part of it; otherwise
    // a highlighted passage that already spans several blocks (it drags as one
    // unit, which is what a Word user expects of a selection); otherwise the
    // one block the grip belongs to.
    const rawIds = piece
      ? piece.ids
      : picked.includes(id)
        ? picked
        : spanned.length > 1 && spanned.includes(id)
          ? spanned
          : [id];
    // A Ctrl+G group drags whole — preview, selection and drop all see every member.
    const grouped = expandToGroups(editor.state.doc, rawIds);
    const ids = topBlocks(editor.state)
      .map((b) => b.id)
      .filter((bid) => grouped.has(bid));

    const levelOf = new Map(
      topBlocks(editor.state).map((b) => [b.id, (b.node.attrs.level as number) || 1])
    );
    const rects = blockEls(editor).map((el) => {
      const r = el.getBoundingClientRect();
      const bid = el.getAttribute('data-block-id') || '';
      return {
        id: bid,
        top: r.top,
        bottom: r.bottom,
        group: groupOfEl(el),
        level: levelOf.get(bid) || 1,
      };
    });
    const column = editor.view.dom.getBoundingClientRect();
    const drag: DragState = {
      ids,
      startX: e.clientX,
      startY: e.clientY,
      moved: false,
      rects,
      column: { left: column.left, width: column.width },
    };
    dragRef.current = drag;
    const mods = { ctrl: e.ctrlKey || e.metaKey, shift: e.shiftKey };

    // Module mode only: the part headers, frozen at drag start same as the
    // block rects above — a drag onto one of them (or the empty-part hint
    // that renders as part of it) is a valid drop even where no block rect
    // exists to hit-test against (Module Zones Phase 3 item 1).
    const headers = moduleMode ? headerEls(editor) : [];
    const headerBoxes = headers.map((h) => h.el.getBoundingClientRect());

    // Reads the FROZEN drag, never `dragRef.current` — the ref is cleared on
    // mouse-up before the drop is computed.
    const moving = new Set(drag.ids);
    const leadIsParagraph =
      !!moduleMode && (drag.rects.find((r) => moving.has(r.id))?.level ?? 1) > 2;
    const lineFor = (clientY: number): DropLine => {
      const { rects: rs, column: col } = drag;
      let index = rs.length;
      for (let i = 0; i < rs.length; i++) {
        if (clientY < rs[i].top + (rs[i].bottom - rs[i].top) / 2) {
          index = i;
          break;
        }
      }
      // Never between two blocks of someone else's group — except, on a
      // module canvas, the line directly under an article heading.
      index = snapLineIndex(rs, index, moving, leadIsParagraph);
      // A piece stays inside its own Part.
      if (piece) index = Math.max(piece.bounds[0], Math.min(piece.bounds[1], index));
      let y = index < rs.length ? rs[index].top : rs.length ? rs[rs.length - 1].bottom : 0;
      // A gap with a part header in it lands ABOVE that header (the block
      // takes the part it follows), so the line is drawn where it lands.
      if (index < rs.length) {
        const above = index > 0 ? rs[index - 1].bottom : -Infinity;
        const box = headerBoxes.find((b) => b.top >= above - 1 && b.bottom <= rs[index].top + 1);
        if (box) y = box.top;
      }
      return { index, y, left: col.left, width: col.width };
    };

    let hoveredHeaderEl: HTMLElement | null = null;
    const setHeaderHover = (next: HTMLElement | null) => {
      if (hoveredHeaderEl === next) return;
      hoveredHeaderEl?.classList.remove('is-drop-target');
      next?.classList.add('is-drop-target');
      hoveredHeaderEl = next;
    };

    const onMove = (ev: MouseEvent) => {
      if (
        !drag.moved &&
        Math.abs(ev.clientX - drag.startX) < DRAG_THRESHOLD &&
        Math.abs(ev.clientY - drag.startY) < DRAG_THRESHOLD
      ) {
        return;
      }
      if (!drag.moved) {
        drag.moved = true;
        document.body.classList.add('sps-blk-dragging');
        // The run being dragged is what is selected, so the eye follows it.
        setBlockSelection(editor.state, dispatch, drag.ids, drag.ids[0]);
      }
      const hit = headerAt(ev.clientY, headers);
      setHeaderHover(hit?.el ?? null);
      setDrop(hit ? null : lineFor(ev.clientY));
    };

    const onUp = (ev: MouseEvent) => {
      document.removeEventListener('mousemove', onMove, true);
      document.removeEventListener('mouseup', onUp, true);
      document.body.classList.remove('sps-blk-dragging');
      setHeaderHover(null);
      dragRef.current = null;
      setDrop(null);
      if (!drag.moved) {
        // A tag press that never travelled is the tag's own click.
        if (piece) return;
        // A press that never travelled is a CLICK: Explorer rules.
        clickBlockHandle(editor.state, dispatch, id, mods);
        focusSheet(editor);
        return;
      }
      const hit = headerAt(ev.clientY, headers);
      if (moduleMode) {
        if (hit) {
          // An article run: end of that part. A paragraph: first block under
          // that part's first article, or pinned under an empty part's
          // header (TWEAKS item 10).
          dropModuleBlocksOnPart(editor.state, dispatch, drag.ids, hit.part);
        } else {
          moveModuleBlocksTo(editor.state, dispatch, drag.ids, lineFor(ev.clientY).index);
        }
      } else {
        moveBlocksTo(editor.state, dispatch, drag.ids, lineFor(ev.clientY).index);
      }
      // The moved run stays selected — the user is usually about to move it
      // again, or to act on it.
      setBlockSelection(editor.state, dispatch, drag.ids, drag.ids[0]);
      focusSheet(editor);
    };

    document.addEventListener('mousemove', onMove, true);
    document.addEventListener('mouseup', onUp, true);
  };

  /* ─── A module piece's name tag is a drag handle (spec canvas only) ─── */
  const beginDragRef = React.useRef(beginDrag);
  beginDragRef.current = beginDrag;
  React.useEffect(() => {
    if (moduleMode || !canEdit) return;
    const onDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      const target = e.target as HTMLElement | null;
      const tag =
        target && typeof target.closest === 'function'
          ? target.closest<HTMLElement>('[data-sf-mod-tag]')
          : null;
      if (!tag || !(editor.view.dom as HTMLElement).contains(tag)) return;
      const piece = insertionPiece(
        editor.state,
        tag.getAttribute('data-sf-mod-tag') || '',
        tag.getAttribute('data-sf-mod-run') || ''
      );
      if (!piece) return;
      beginDragRef.current(e, piece.ids[0], piece);
    };
    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [editor, canEdit, moduleMode]);

  /* ─── The toolbox's "New paragraph" drag lands here too (Module Zones
         Phase 2) — native HTML5 drag-and-drop, not the mouse-based grip
         drag above, because it starts OUTSIDE the canvas (the Blocks
         toolbox). The three zones highlight the same way a gutter drag's
         header hit does; a header/empty-hint drop always lands at that
         part's end, a body drop lands exactly where the pointer is and
         infers its part from its new neighbors (`insertModuleArticle`). ─── */
  React.useEffect(() => {
    if (!moduleMode || !canEdit) return;
    let hoveredHeaderEl: HTMLElement | null = null;
    const setHeaderHover = (next: HTMLElement | null) => {
      if (hoveredHeaderEl === next) return;
      hoveredHeaderEl?.classList.remove('is-drop-target');
      next?.classList.add('is-drop-target');
      hoveredHeaderEl = next;
    };
    const isOurDrag = (e: DragEvent) =>
      !!e.dataTransfer && Array.from(e.dataTransfer.types).includes(NEW_PARAGRAPH_DRAG_MIME);
    const inCanvas = (e: DragEvent) => {
      const r = (editor.view.dom as HTMLElement).getBoundingClientRect();
      return (
        e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom
      );
    };
    const clear = () => {
      setHeaderHover(null);
      setDrop(null);
    };
    const onDragOver = (e: DragEvent) => {
      if (!isOurDrag(e)) return;
      if (!inCanvas(e)) {
        clear();
        return;
      }
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
      const hit = headerAt(e.clientY, headerEls(editor));
      setHeaderHover(hit?.el ?? null);
      if (hit) {
        setDrop(null);
      } else {
        const index = liveLineIndex(editor, e.clientY);
        const els = blockEls(editor);
        const col = (editor.view.dom as HTMLElement).getBoundingClientRect();
        const y = index < els.length ? els[index].getBoundingClientRect().top : col.bottom;
        setDrop({ index, y, left: col.left, width: col.width });
      }
    };
    const onDrop = (e: DragEvent) => {
      if (!isOurDrag(e)) return;
      e.preventDefault();
      const landed = inCanvas(e);
      const hit = landed ? headerAt(e.clientY, headerEls(editor)) : null;
      clear();
      if (!landed) return; // drop-outside cancels, no state change
      if (hit) {
        const index = partLandingIndex(editor.state, hit.part);
        insertModuleArticle(editor.state, dispatch, index, hit.part);
      } else {
        insertModuleArticle(editor.state, dispatch, liveLineIndex(editor, e.clientY));
      }
    };
    document.addEventListener('dragover', onDragOver);
    document.addEventListener('drop', onDrop);
    document.addEventListener('dragend', clear);
    return () => {
      document.removeEventListener('dragover', onDragOver);
      document.removeEventListener('drop', onDrop);
      document.removeEventListener('dragend', clear);
      clear();
    };
  }, [moduleMode, canEdit, editor, dispatch]);

  if (!canEdit) return null;

  const count = sel.ids.length;

  return createPortal(
    <div className="sps-blk-gutter">
      {brackets.map((bar) => (
        <div
          key={bar.groupId}
          className="sps-blk-bracket"
          style={{ left: bar.x, top: bar.top, height: bar.height }}
          aria-hidden="true"
        />
      ))}

      {spots.map((spot) => {
        const inSel = spot.selected && count > 1;
        const label =
          spot.size > 1
            ? `Group of ${spot.size} blocks — drag to move them together, Ctrl+Shift+G to ungroup`
            : inSel
              ? `${count} blocks selected — drag to move, click to select this one only`
              : 'Select block — drag to move, Ctrl-click to add, Shift-click for a range';
        return (
          <button
            key={spot.id}
            type="button"
            className={`sps-blk-grip${spot.selected ? ' is-selected' : ''}${
              spot.size > 1 ? ' is-group' : ''
            }`}
            style={{ left: spot.x, top: spot.y }}
            data-grip-block={spot.id}
            aria-label={label}
            aria-pressed={spot.selected}
            title={label}
            onMouseDown={(e) => beginDrag(e, spot.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                clickBlockHandle(editor.state, dispatch, spot.id, {
                  ctrl: e.ctrlKey || e.metaKey,
                  shift: e.shiftKey,
                });
                return;
              }
              // The grips are PORTALLED to <body>, so a keydown on one never
              // reaches the editor's own keymap. Delete, the clipboard triad,
              // group/ungroup and the move chord are all routed by hand to the
              // SAME commands the keymap runs (Tranche 4 item 6; group/move
              // joined them for the "Ctrl+G only works from the document"
              // repair) — copy and cut through a real platform clipboard
              // event on the focused sheet, paste through prosemirror-view's
              // own paste pipeline. `canEdit` false means this gutter is not
              // rendered at all, so none of these are reachable.
              if (!canEdit) return;
              const mod = e.ctrlKey || e.metaKey;
              const key = e.key.toLowerCase();
              if (e.key === 'Delete' || e.key === 'Backspace') {
                e.preventDefault();
                deleteSelectedBlocks(editor.state, dispatch);
                editor.view.focus();
                return;
              }
              if (mod && (key === 'c' || key === 'x')) {
                e.preventDefault();
                runClipboardCommand(editor.view, key === 'c' ? 'copy' : 'cut');
                return;
              }
              if (mod && key === 'v') {
                e.preventDefault();
                void pasteFromSystemClipboard(editor.view);
                return;
              }
              if (mod && key === 'g') {
                e.preventDefault();
                if (e.shiftKey) {
                  ungroupSelectedBlocks(editor.state, dispatch);
                } else {
                  const refusal = groupSelectedBlocks(editor.state, dispatch);
                  if (refusal === 'module') onNotice?.(GROUP_MODULE_REFUSAL);
                }
                // A group keeps only its first block's grip, so this button can
                // unmount under the keystroke — hand focus to the sheet so the
                // next chord still lands.
                focusSheet(editor);
                return;
              }
              if (e.altKey && e.shiftKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
                e.preventDefault();
                moveSelectedBlocks(editor.state, dispatch, e.key === 'ArrowUp' ? -1 : 1);
                focusSheet(editor);
              }
            }}
            onContextMenu={(e) => e.preventDefault()}
          >
            <span className="sps-blk-grip-dot">
              <Icon icon={GripVertical} em={1.0769} aria-hidden="true" />
            </span>
          </button>
        );
      })}

      {drop && (
        <div
          className="sps-blk-drop"
          style={{ left: drop.left, top: drop.y, width: drop.width }}
          aria-hidden="true"
        />
      )}
    </div>,
    document.body
  );
}
